import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToastContext } from '../shared/context/ToastContext';
import { useAuth } from '../shared/context/AuthContext';
import './CampaignDetail.css';
import { apiFetch } from '../shared/api';

function CampaignDetail() {
  const META_LIMITS = {
    primaryDescription: 125,
    headline: 40,
    description: 30
  };

  const META_HARD_LIMITS = {
    primaryDescription: 125,
    headline: 100,
    description: 100
  };

  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();
  const { isAdmin } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [feedDomains, setFeedDomains] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPosterFiles, setUploadPosterFiles] = useState([]);
  const [uploadKind, setUploadKind] = useState('mixed');
  const [uploadData, setUploadData] = useState({});
  const [editImageData, setEditImageData] = useState({});
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkEditRows, setBulkEditRows] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);

  const normalizeTags = (rawTags) => {
    if (!rawTags) return [];
    if (Array.isArray(rawTags)) {
      return rawTags.map((tag) => String(tag).trim()).filter(Boolean);
    }
    if (typeof rawTags === 'string') {
      return rawTags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  };

  const getExceededMessage = (value, limit) => {
    const length = (value || '').length;
    if (length <= limit) return '';
    return `${length - limit} has been exceeded`;
  };

  const isOverHardLimit = (value, limit) => {
    const length = (value || '').length;
    return length > limit;
  };

  const formatInMaltaTimezone = (value) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Malta',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    }).format(parsed);
  };

  useEffect(() => {
    fetchCampaign();
    fetchImages();
  }, [id]);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const response = await apiFetch('/clients');
        if (!response.ok) return;
        const data = await response.json();
        setClients(data);
      } catch {
        // ignore
      }
    })();
  }, [isAdmin]);

  useEffect(() => {
    (async () => {
      try {
        const clientId = formData.client_id || campaign?.client_id;
        if (!clientId) {
          setBrands([]);
          return;
        }
        const response = await apiFetch(`/brands?client_id=${encodeURIComponent(clientId)}`);
        if (!response.ok) return;
        const data = await response.json();
        setBrands(data);
        setFormData((prev) => {
          if (!prev) return prev;
          if (prev.brand && data.includes(prev.brand)) return prev;
          return { ...prev, brand: data[0] || '' };
        });
      } catch {
        // ignore
      }
    })();
  }, [formData.client_id, campaign?.client_id]);

  useEffect(() => {
    (async () => {
      try {
        const clientId = formData.client_id || campaign?.client_id;
        if (!clientId) {
          setFeedDomains([]);
          return;
        }
        const response = await apiFetch(`/feed-domains?client_id=${encodeURIComponent(clientId)}`);
        if (!response.ok) return;
        const data = await response.json();
        setFeedDomains(data);
      } catch {
        // ignore
      }
    })();
  }, [formData.client_id, campaign?.client_id]);

  const fetchCampaign = async () => {
    try {
      const response = await apiFetch(`/campaigns/${id}`);
      if (!response.ok) throw new Error('Failed to fetch campaign');
      const data = await response.json();
      setCampaign(data);
      setFormData(data);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchImages = async () => {
    try {
      const response = await apiFetch(`/images/campaign/${id}`);
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();
      console.log('Fetched images with dates:', data.map(img => ({
        id: img.id,
        sale_start_date: img.sale_start_date,
        sale_end_date: img.sale_end_date,
        hasStart: !!img.sale_start_date,
        hasEnd: !!img.sale_end_date
      })));
      setImages(data);
    } catch (err) {
      console.error('Error fetching images:', err);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const campaignPrimaryHardExceeded = isOverHardLimit(
      formData.custom_label_3,
      META_HARD_LIMITS.primaryDescription
    );
    const campaignHeadlineHardExceeded = isOverHardLimit(
      formData.custom_label_1,
      META_HARD_LIMITS.headline
    );
    const campaignDescriptionHardExceeded = isOverHardLimit(
      formData.custom_label_2,
      META_HARD_LIMITS.description
    );
    if (campaignPrimaryHardExceeded || campaignHeadlineHardExceeded || campaignDescriptionHardExceeded) {
      showError('Character limit exceeded. Please review warning messages.');
      return;
    }
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        link: formData.link,
        brand: formData.brand,
        client_id: formData.client_id,
        feed_domain_id: formData.feed_domain_id || null,
        default_label_1_headline: formData.custom_label_1 || '',
        default_label_2_description: formData.custom_label_2 || '',
        default_primary_description: formData.custom_label_3 || ''
      };
      const response = await apiFetch(`/campaigns/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to update campaign');
      }
      const updated = await response.json();
      setCampaign(updated);
      setShowEditForm(false);
      success('Campaign updated successfully');
    } catch (err) {
      showError(err.message);
    }
  };

  const handlePushDefaultToCreatives = async (field, value) => {
    const hardLimits = {
      primary_description: META_HARD_LIMITS.primaryDescription,
      label_1_headline: META_HARD_LIMITS.headline,
      label_2_description: META_HARD_LIMITS.description
    };
    const hardLimit = hardLimits[field];
    if (hardLimit && isOverHardLimit(value, hardLimit)) {
      showError('Character limit exceeded. Please review warning messages.');
      return;
    }
    try {
      const response = await apiFetch(`/campaigns/${id}/push-default-to-creatives`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ field, value: value || null })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to push updates');
      await fetchImages();
      success('Pushed to all creatives');
    } catch (err) {
      showError(err.message);
    }
  };

  const handleImageUpload = async (e) => {
    e.preventDefault();
    if (uploading) return;
    const selectedVideoCount = uploadFiles.filter(
      (file) => file.type.startsWith('video/') || /\.mp4$/i.test(file.name)
    ).length;

    if (uploadFiles.length === 0) {
      showError(
        uploadKind === 'video'
          ? 'Please select one or more MP4 files'
          : uploadKind === 'image'
            ? 'Please select one or more image files'
            : 'Please select one or more image/video files'
      );
      return;
    }
    if (
      selectedVideoCount > 0 &&
      uploadPosterFiles.length > 1 &&
      uploadPosterFiles.length !== selectedVideoCount
    ) {
      showError('For videos, upload one shared poster or one poster per video');
      return;
    }

    const formData = new FormData();
    uploadFiles.forEach((file) => {
      formData.append('asset', file);
    });
    uploadPosterFiles.forEach((file) => {
      formData.append('poster', file);
    });
    if (uploadData.status_override) formData.append('status_override', uploadData.status_override);
    if (uploadData.tags) formData.append('tags', uploadData.tags);
    if (uploadData.custom_label_1) formData.append('label_1_headline', uploadData.custom_label_1);
    if (uploadData.custom_label_2) formData.append('label_2_description', uploadData.custom_label_2);
    if (uploadData.custom_label_3) formData.append('primary_description', uploadData.custom_label_3);
    formData.append('sale_start_date', uploadData.sale_start_date || '');
    formData.append('sale_end_date', uploadData.sale_end_date || '');

    setUploading(true);
    try {
      const response = await apiFetch(`/images/upload/${id}`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to upload asset');
      }
      const count = uploadFiles.length;
      await fetchImages();
      setShowImageUpload(false);
      setUploadFiles([]);
      setUploadPosterFiles([]);
      setUploadData({});
      success(
        uploadKind === 'video'
          ? `${count} video${count > 1 ? 's' : ''} uploaded successfully`
          : uploadKind === 'image'
            ? `${count} image${count > 1 ? 's' : ''} uploaded successfully`
            : `${count} creative${count > 1 ? 's' : ''} uploaded successfully`
      );
    } catch (err) {
      showError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
    // Auto-set aspect ratio if not already set (Custom Label [0])
    const imageSize = image.custom_label_0 || image.product_tag_0 || '';
    setEditImageData({
      link: image.link || '',
      brand: image.brand || '',
      custom_label_0: imageSize,
      custom_label_1: image.custom_label_1 || image.product_tag_1 || '',
      custom_label_2: image.custom_label_2 || image.title || '',
      custom_label_3: image.custom_label_3 || image.description || '',
      status_override: image.status_override || '',
      tags: normalizeTags(image.tags).join(', '),
      sale_start_date: image.sale_start_date ? (() => {
        try {
          const dateStr = String(image.sale_start_date);
          // If already in datetime-local format (YYYY-MM-DDTHH:mm), use as-is
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
            return dateStr;
          }
          // Try to parse as date
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) {
            console.warn('Invalid start date format:', dateStr);
            return '';
          }
          // Convert to datetime-local format using local time
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (e) {
          console.error('Error parsing start date:', e, image.sale_start_date);
          return '';
        }
      })() : '',
      sale_end_date: image.sale_end_date ? (() => {
        try {
          const dateStr = String(image.sale_end_date);
          // If already in datetime-local format (YYYY-MM-DDTHH:mm), use as-is
          if (dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
            return dateStr;
          }
          // Try to parse as date
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) {
            console.warn('Invalid end date format:', dateStr);
            return '';
          }
          // Convert to datetime-local format using local time
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (e) {
          console.error('Error parsing end date:', e, image.sale_end_date);
          return '';
        }
      })() : ''
    });
  };

  const handleUpdateImage = async (e) => {
    e.preventDefault();
    await saveEditingImage();
  };

  const saveEditingImage = async () => {
    if (!editingImage) return false;
    const primaryHardExceeded = isOverHardLimit(
      editImageData.custom_label_3,
      META_HARD_LIMITS.primaryDescription
    );
    const headlineHardExceeded = isOverHardLimit(editImageData.custom_label_1, META_HARD_LIMITS.headline);
    const descriptionHardExceeded = isOverHardLimit(
      editImageData.custom_label_2,
      META_HARD_LIMITS.description
    );
    if (primaryHardExceeded || headlineHardExceeded || descriptionHardExceeded) {
      showError('Character limit exceeded. Please review warning messages.');
      return false;
    }
    try {
      // Prepare data with proper date handling
      const updateData = {
        link: editImageData.link || null,
        brand: editImageData.brand || null,
        custom_label_0: editImageData.custom_label_0 || null,
        label_1_headline: editImageData.custom_label_1 || null,
        label_2_description: editImageData.custom_label_2 || null,
        primary_description: editImageData.custom_label_3 || null,
        status_override: editImageData.status_override === 'archived' ? 'archived' : null,
        tags: normalizeTags(editImageData.tags),
        // Send dates - preserve the actual values from the form
        // If the value exists and is not empty, send it; otherwise send null
        sale_start_date: (editImageData.sale_start_date && editImageData.sale_start_date.trim() !== '') 
          ? editImageData.sale_start_date.trim() 
          : null,
        sale_end_date: (editImageData.sale_end_date && editImageData.sale_end_date.trim() !== '') 
          ? editImageData.sale_end_date.trim() 
          : null
      };
      
      console.log('Edit image data state:', editImageData);
      console.log('Sending update data:', updateData);
      console.log('Date values being sent:', {
        sale_start_date: updateData.sale_start_date,
        sale_end_date: updateData.sale_end_date,
        startType: typeof updateData.sale_start_date,
        endType: typeof updateData.sale_end_date,
        startLength: updateData.sale_start_date ? updateData.sale_start_date.length : 0,
        endLength: updateData.sale_end_date ? updateData.sale_end_date.length : 0
      });
      
      const response = await apiFetch(`/images/${editingImage.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update image');
      }
      
      const updatedImage = await response.json();
      console.log('Updated image received:', updatedImage);
      console.log('Dates in response:', {
        sale_start_date: updatedImage.sale_start_date,
        sale_end_date: updatedImage.sale_end_date,
        hasStart: !!updatedImage.sale_start_date,
        hasEnd: !!updatedImage.sale_end_date
      });
      
      await fetchImages();
      setEditingImage(null);
      setEditImageData({});
      success('Image updated successfully');
      return true;
    } catch (err) {
      console.error('Error updating image:', err);
      showError(err.message);
      return false;
    }
  };

  const handleBackdropClose = async () => {
    const didSave = await saveEditingImage();
    if (!didSave) return;
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      const response = await apiFetch(`/images/${imageId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete image');
      await fetchImages();
      success('Image deleted successfully');
    } catch (err) {
      showError(err.message);
    }
  };

  const copyFeedUrl = () => {
    const feedUrl = campaign?.feed_url || '';
    if (!feedUrl) return;
    navigator.clipboard.writeText(feedUrl);
    success('Feed URL copied to clipboard');
  };

  const openBulkEdit = () => {
    const rows = images.map((image) => ({
      id: image.id,
      original_filename: image.original_filename,
      public_url: image.public_url,
      link: image.link || '',
      brand: image.brand || '',
      custom_label_0: image.custom_label_0 || image.product_tag_0 || '',
      status_override: image.status_override || '',
      tags: normalizeTags(image.tags),
      sale_start_date: image.sale_start_date || null,
      sale_end_date: image.sale_end_date || null,
      description_primary: image.custom_label_3 || image.description || '',
      custom_label_1: image.custom_label_1 || image.product_tag_1 || '',
      custom_label_2: image.custom_label_2 || image.title || ''
    }));
    setBulkEditRows(rows);
    setShowBulkEdit(true);
  };

  const updateBulkRow = (id, field, value) => {
    setBulkEditRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const saveBulkEdit = async () => {
    if (bulkEditRows.length === 0) return;
    for (const row of bulkEditRows) {
      if (
        isOverHardLimit(row.description_primary, META_HARD_LIMITS.primaryDescription) ||
        isOverHardLimit(row.custom_label_1, META_HARD_LIMITS.headline) ||
        isOverHardLimit(row.custom_label_2, META_HARD_LIMITS.description)
      ) {
        showError('Character limit exceeded. Please review warning messages.');
        return;
      }
    }

    setBulkSaving(true);
    try {
      const updates = bulkEditRows.map((row) =>
        apiFetch(`/images/${row.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            link: row.link || null,
            brand: row.brand || null,
            custom_label_0: row.custom_label_0 || null,
            status_override: row.status_override === 'archived' ? 'archived' : null,
            tags: row.tags,
            sale_start_date: row.sale_start_date || null,
            sale_end_date: row.sale_end_date || null,
            primary_description: row.description_primary || null,
            label_1_headline: row.custom_label_1 || null,
            label_2_description: row.custom_label_2 || null
          })
        })
      );
      const responses = await Promise.all(updates);
      const failed = responses.find((res) => !res.ok);
      if (failed) {
        const data = await failed.json().catch(() => ({}));
        throw new Error(data.error || 'One or more creatives failed to update');
      }
      await fetchImages();
      setShowBulkEdit(false);
      success(`Updated ${bulkEditRows.length} creative${bulkEditRows.length === 1 ? '' : 's'}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkBackdropClose = async () => {
    if (bulkSaving) return;
    await saveBulkEdit();
  };

  if (loading) {
    return <div className="loading">Loading campaign...</div>;
  }

  if (!campaign) {
    return (
      <div className="alert alert-error">
        <p>Campaign not found</p>
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          Back to Campaigns
        </button>
      </div>
    );
  }

  return (
    <div className="campaign-detail">
      <div className="campaign-detail-header">
        <div>
          <h2>{campaign.name}</h2>
          <p className="campaign-meta">
            Created: {new Date(campaign.created_at).toLocaleString()}
            {campaign.updated_at && campaign.updated_at !== campaign.created_at && (
              <span> • Updated: {new Date(campaign.updated_at).toLocaleString()}</span>
            )}
          </p>
        </div>
        <div className="header-actions">
          <button onClick={() => setShowEditForm(!showEditForm)} className="btn btn-secondary">
            {showEditForm ? 'Cancel Edit' : 'Edit Campaign'}
          </button>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            ← Home
          </button>
        </div>
      </div>

      {showEditForm ? (
        <div className="card">
          <h3>Edit Campaign</h3>
          <form onSubmit={handleUpdate}>
            <div className="form-group campaign-name-group">
              <label>Campaign Name</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Brand</label>
                <select
                  value={formData.brand || ''}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  required
                >
                  {!formData.brand && (
                    <option value="" disabled>
                      {brands.length === 0 ? 'No brands configured' : 'Select a brand'}
                    </option>
                  )}
                  {formData.brand && !brands.includes(formData.brand) && (
                    <option value={formData.brand}>{formData.brand}</option>
                  )}
                  {brands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Description (Local Campaign Notes)</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Catalogue Description"
                />
              </div>
            </div>

            <p className="form-help">
              Brand and Description are local campaign notes. Primary Description and Custom Labels below are defaults inherited by creatives.
            </p>

            <div className="form-group">
              <div className="field-action-row">
                <label>Description (Primary)</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePushDefaultToCreatives('primary_description', formData.custom_label_3)}
                >
                  Push to all creatives
                </button>
              </div>
              <textarea
                value={formData.custom_label_3 || ''}
                onChange={(e) => setFormData({ ...formData, custom_label_3: e.target.value })}
              />
              {getExceededMessage(formData.custom_label_3, META_LIMITS.primaryDescription) && (
                <small style={{ color: 'var(--color-destructive)', fontSize: '0.85rem' }}>
                  {getExceededMessage(formData.custom_label_3, META_LIMITS.primaryDescription)}
                </small>
              )}
            </div>

            <div className="form-group">
              <div className="field-action-row">
                <label>Custom Label [1] (Headline)</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePushDefaultToCreatives('label_1_headline', formData.custom_label_1)}
                >
                  Push to all creatives
                </button>
              </div>
              <input
                type="text"
                value={formData.custom_label_1 || ''}
                onChange={(e) => setFormData({ ...formData, custom_label_1: e.target.value })}
              />
              {getExceededMessage(formData.custom_label_1, META_LIMITS.headline) && (
                <small style={{ color: 'var(--color-destructive)', fontSize: '0.85rem' }}>
                  {getExceededMessage(formData.custom_label_1, META_LIMITS.headline)}
                </small>
              )}
            </div>

            <div className="form-group">
              <div className="field-action-row">
                <label>Custom Label [2] (Description)</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePushDefaultToCreatives('label_2_description', formData.custom_label_2)}
                >
                  Push to all creatives
                </button>
              </div>
              <input
                type="text"
                value={formData.custom_label_2 || ''}
                onChange={(e) => setFormData({ ...formData, custom_label_2: e.target.value })}
              />
              {getExceededMessage(formData.custom_label_2, META_LIMITS.description) && (
                <small style={{ color: 'var(--color-destructive)', fontSize: '0.85rem' }}>
                  {getExceededMessage(formData.custom_label_2, META_LIMITS.description)}
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Link *</label>
              <input
                type="url"
                value={formData.link || ''}
                onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                required
              />
            </div>

            {isAdmin && (
              <div className="form-group">
                <label>Client</label>
                <select
                  value={formData.client_id || 'unassigned'}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value, brand: '', feed_domain_id: '' })}
                >
                  <option value="unassigned">Unassigned</option>
                  {clients.filter((client) => client.id !== 'unassigned').map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Feed Domain (optional)</label>
              <select
                value={formData.feed_domain_id || ''}
                onChange={(e) => setFormData({ ...formData, feed_domain_id: e.target.value })}
              >
                <option value="">Use current API host</option>
                {feedDomains.map((domain) => (
                  <option key={domain.id} value={domain.id}>{domain.host}</option>
                ))}
              </select>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Save Changes</button>
            </div>
          </form>
        </div>
      ) : (
        <div className="card">
          <h3>Campaign Details</h3>
          <div className="campaign-info">
            <p><strong>Description:</strong> {campaign.description || 'Not set'}</p>
            <p><strong>Brand:</strong> {campaign.brand || 'Not set'}</p>
            <p><strong>Description (Primary):</strong> {campaign.custom_label_3 || 'Not set'}</p>
            <p><strong>Custom Label [1] (Headline):</strong> {campaign.custom_label_1 || 'Not set'}</p>
            <p><strong>Custom Label [2] (Description):</strong> {campaign.custom_label_2 || 'Not set'}</p>
            {isAdmin && (
              <p>
                <strong>Client:</strong>{' '}
                <span className="client-label">{campaign.client_name || 'Unassigned'}</span>
              </p>
            )}
            <p><strong>Feed Domain:</strong> {campaign.feed_host || 'Current API host'}</p>
            <p><strong>Price:</strong> 10.00 USD</p>
            <p><strong>Link:</strong> {campaign.link ? <a href={campaign.link} target="_blank" rel="noopener noreferrer">{campaign.link}</a> : 'Not set'}</p>
            <p><strong>Availability:</strong> In Stock</p>
            <p><strong>Condition:</strong> New</p>
          </div>
        </div>
      )}

      <div className="card">
        <div className="feed-url-section">
          <h3>XML Feed URL</h3>
          <p>Use this URL to upload to Facebook catalog:</p>
          <div className="feed-url">
            <div className="feed-url-with-copy">
              <code>{campaign.feed_url || ''}</code>
              <button
                onClick={copyFeedUrl}
                className="btn-copy"
                title="Copy URL"
              >
                📋 Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="images-section-header">
          <div>
            <h3>Creatives</h3>
            <p style={{ color: 'var(--color-muted-foreground)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
              {images.length} {images.length === 1 ? 'item' : 'items'} uploaded
            </p>
          </div>
          <div className="images-header-actions">
            <button
              onClick={openBulkEdit}
              className="btn btn-secondary"
              disabled={images.length === 0}
            >
              Bulk edit
            </button>
            <button onClick={() => setShowImageUpload(!showImageUpload)} className="btn btn-primary">
              {showImageUpload ? 'Cancel Upload' : '+ Upload creative'}
            </button>
          </div>
        </div>

        {showImageUpload && (
          <form onSubmit={handleImageUpload} className="upload-form">
            <div className="form-group">
              <label>Type</label>
              <select
                value={uploadKind}
                onChange={(e) => {
                  setUploadKind(e.target.value);
                  setUploadFiles([]);
                  setUploadPosterFiles([]);
                }}
              >
                <option value="mixed">Mixed (images + videos)</option>
                <option value="image">Images only (JPEG, PNG, GIF)</option>
                <option value="video">Videos only (MP4)</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                {uploadKind === 'video'
                  ? 'Video file(s) (MP4) *'
                  : uploadKind === 'image'
                    ? 'Image file(s) *'
                    : 'Creative file(s): images and videos *'}
              </label>
              <input
                type="file"
                accept={uploadKind === 'video' ? 'video/mp4,.mp4' : uploadKind === 'image' ? 'image/jpeg,image/png,image/gif' : 'image/jpeg,image/png,image/gif,video/mp4,.mp4'}
                multiple
                onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                required
              />
              <p className="form-help">
                {uploadKind === 'video'
                  ? `${uploadFiles.length} video file${uploadFiles.length === 1 ? '' : 's'} selected`
                  : uploadKind === 'image'
                    ? `${uploadFiles.length} image file${uploadFiles.length === 1 ? '' : 's'} selected`
                    : `${uploadFiles.length} creative file${uploadFiles.length === 1 ? '' : 's'} selected`}
              </p>
            </div>
            <div className="form-group">
              <label>Poster image(s) (optional for videos)</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                multiple
                onChange={(e) => setUploadPosterFiles(Array.from(e.target.files || []))}
              />
              <p className="form-help">
                Optional: upload one shared poster for all videos, or one poster per video.
              </p>
              <p className="form-help">
                {uploadPosterFiles.length} poster file{uploadPosterFiles.length === 1 ? '' : 's'} selected
              </p>
            </div>
            <p className="form-help">Optional: Override campaign values for this creative</p>
            <div className="form-group">
              <label>Status Override</label>
              <select
                value={uploadData.status_override || ''}
                onChange={(e) => setUploadData({ ...uploadData, status_override: e.target.value })}
              >
                <option value="">Auto (date range based)</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input
                type="text"
                value={uploadData.tags || ''}
                onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                placeholder="e.g. UGC, Spring, Retargeting"
              />
            </div>
            <div className="form-group">
              <label>Description (Primary) (override)</label>
              <textarea
                value={uploadData.custom_label_3 || ''}
                onChange={(e) => setUploadData({ ...uploadData, custom_label_3: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Custom Label [1] (Headline) (override)</label>
              <input
                type="text"
                value={uploadData.custom_label_1 || ''}
                onChange={(e) => setUploadData({ ...uploadData, custom_label_1: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Custom Label [2] (Description) (override)</label>
              <input
                type="text"
                value={uploadData.custom_label_2 || ''}
                onChange={(e) => setUploadData({ ...uploadData, custom_label_2: e.target.value })}
              />
            </div>
            <p className="form-help" style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--color-muted-foreground)' }}>
              Note: Custom Label [0] (Aspect Ratio) is auto-captured (e.g. 1:1, 4:5, 9:16) but can be overwritten
            </p>
            <div className="form-section">
              <h4 style={{ marginBottom: '1rem', color: 'var(--color-primary)' }}>Sale Price Effective Date</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Sale Start Date</label>
                  <input
                    type="datetime-local"
                    value={uploadData.sale_start_date || ''}
                    onChange={(e) => setUploadData({ ...uploadData, sale_start_date: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Sale End Date</label>
                  <input
                    type="datetime-local"
                    value={uploadData.sale_end_date || ''}
                    onChange={(e) => setUploadData({ ...uploadData, sale_end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary btn-with-spinner"
              disabled={uploading || uploadFiles.length === 0}
              aria-busy={uploading}
            >
              {uploading && <span className="btn-spinner" aria-hidden="true" />}
              {uploading
                ? `Uploading${uploadFiles.length > 1 ? ` ${uploadFiles.length} files` : ''}...`
                : uploadKind === 'video'
                  ? `Upload ${uploadFiles.length || ''} video${uploadFiles.length === 1 ? '' : 's'}`
                  : uploadKind === 'image'
                    ? `Upload ${uploadFiles.length || ''} image${uploadFiles.length === 1 ? '' : 's'}`
                    : `Upload ${uploadFiles.length || ''} creative${uploadFiles.length === 1 ? '' : 's'}`}
            </button>
          </form>
        )}

        {images.length === 0 ? (
          <div className="empty-state">
            <p>No creatives uploaded yet. Upload an image or video to get started.</p>
          </div>
        ) : (
          <div className="image-grid">
            {images.map(image => (
              <div key={image.id} className="image-card">
                {image.asset_type === 'video' && image.public_url ? (
                  <video
                    src={image.public_url}
                    controls
                    muted
                    playsInline
                    style={{ width: '100%', maxHeight: '220px', objectFit: 'contain', background: 'var(--color-popover)' }}
                  />
                ) : (
                  <img
                    src={image.public_url || ''}
                    alt={image.original_filename}
                  />
                )}
                <div className="image-card-content">
                  <h4>{image.original_filename}</h4>
                  <p>
                    <strong>Status:</strong>{' '}
                    <span className={`status-badge status-${image.status || 'inactive'}`}>
                      {image.status || 'inactive'}
                    </span>
                  </p>
                  {normalizeTags(image.tags).length > 0 && (
                    <div className="tag-list">
                      {normalizeTags(image.tags).map((tag) => (
                        <span key={`${image.id}-${tag}`} className="tag-pill">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {image.asset_type === 'video' && <p><strong>Type:</strong> video (MP4)</p>}
                  <p>Dimensions: {image.width} × {image.height}px</p>
                  <p>File Size: {(image.file_size / 1024).toFixed(2)} KB</p>
                  <p><strong>Custom Label [0] (Aspect Ratio):</strong> {image.custom_label_0 || image.product_tag_0 || 'Not set'}</p>
                  {(image.custom_label_3 || image.description) && <p><strong>Description (Primary):</strong> {image.custom_label_3 || image.description}</p>}
                  {(image.custom_label_1 || image.product_tag_1) && <p><strong>Custom Label [1] (Headline):</strong> {image.custom_label_1 || image.product_tag_1}</p>}
                  {(image.custom_label_2 || image.title) && <p><strong>Custom Label [2] (Description):</strong> {image.custom_label_2 || image.title}</p>}
                  {image.sale_start_date && (
                    <p><strong>Sale Start:</strong> {formatInMaltaTimezone(image.sale_start_date)}</p>
                  )}
                  {image.sale_end_date && (
                    <p><strong>Sale End:</strong> {formatInMaltaTimezone(image.sale_end_date)}</p>
                  )}
                  <div className="image-actions">
                    <button
                      onClick={() => handleEditImage(image)}
                      className="btn btn-secondary btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingImage && (
        <div className="modal-overlay" onClick={handleBackdropClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Image</h3>
              <button className="modal-close" onClick={() => setEditingImage(null)}>×</button>
            </div>
            <form onSubmit={handleUpdateImage} className="modal-body">
              <div className="form-group">
                <label>Custom Label [0] (Aspect Ratio)</label>
                <input
                  type="text"
                  value={editImageData.custom_label_0 || ''}
                  onChange={(e) => setEditImageData({ ...editImageData, custom_label_0: e.target.value })}
                  placeholder="e.g., 1:1, 4:5, 9:16"
                />
                <small style={{ color: 'var(--color-muted-foreground)', fontSize: '0.85rem' }}>
                  Automatically captured to nearest aspect ratio if blank, but can be overwritten
                </small>
              </div>
              <div className="form-group">
                <label>Status Override</label>
                <select
                  value={editImageData.status_override || ''}
                  onChange={(e) => setEditImageData({ ...editImageData, status_override: e.target.value })}
                >
                  <option value="">Auto (date range based)</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div className="form-group">
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  value={editImageData.tags || ''}
                  onChange={(e) => setEditImageData({ ...editImageData, tags: e.target.value })}
                  placeholder="e.g. UGC, Spring, Retargeting"
                />
              </div>
              <div className="form-group">
                <label>Description (Primary)</label>
                <textarea
                  value={editImageData.custom_label_3 || ''}
                  onChange={(e) => setEditImageData({ ...editImageData, custom_label_3: e.target.value })}
                  placeholder="Enter primary description"
                />
                {getExceededMessage(editImageData.custom_label_3, META_LIMITS.primaryDescription) && (
                  <small style={{ color: 'var(--color-destructive)', fontSize: '0.85rem' }}>
                    {getExceededMessage(editImageData.custom_label_3, META_LIMITS.primaryDescription)}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Custom Label [1] (Headline)</label>
                <input
                  type="text"
                  value={editImageData.custom_label_1 || ''}
                  onChange={(e) => setEditImageData({ ...editImageData, custom_label_1: e.target.value })}
                  placeholder="Enter headline"
                />
                {getExceededMessage(editImageData.custom_label_1, META_LIMITS.headline) && (
                  <small style={{ color: 'var(--color-destructive)', fontSize: '0.85rem' }}>
                    {getExceededMessage(editImageData.custom_label_1, META_LIMITS.headline)}
                  </small>
                )}
              </div>
              <div className="form-group">
                <label>Custom Label [2] (Description)</label>
                <textarea
                  value={editImageData.custom_label_2 || ''}
                  onChange={(e) => setEditImageData({ ...editImageData, custom_label_2: e.target.value })}
                  placeholder="Enter description"
                />
                {getExceededMessage(editImageData.custom_label_2, META_LIMITS.description) && (
                  <small style={{ color: 'var(--color-destructive)', fontSize: '0.85rem' }}>
                    {getExceededMessage(editImageData.custom_label_2, META_LIMITS.description)}
                  </small>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Sale Start Date</label>
                  <input
                    type="datetime-local"
                    value={editImageData.sale_start_date || ''}
                    onChange={(e) => {
                      console.log('Start date changed:', e.target.value);
                      setEditImageData({ ...editImageData, sale_start_date: e.target.value });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Sale End Date</label>
                  <input
                    type="datetime-local"
                    value={editImageData.sale_end_date || ''}
                    onChange={(e) => {
                      console.log('End date changed:', e.target.value);
                      setEditImageData({ ...editImageData, sale_end_date: e.target.value });
                    }}
                  />
                </div>
              </div>
              <p className="form-help" style={{ marginTop: '0.25rem' }}>
                Timezone: Europe/Malta (auto DST)
              </p>
              <div className="modal-actions">
                <button type="button" onClick={() => setEditingImage(null)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkEdit && (
        <div className="modal-overlay" onClick={handleBulkBackdropClose}>
          <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Edit Creatives</h3>
              <div className="bulk-modal-header-actions">
                <button
                  type="button"
                  onClick={() => setShowBulkEdit(false)}
                  className="btn btn-secondary btn-sm"
                  disabled={bulkSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveBulkEdit}
                  className="btn btn-primary btn-sm"
                  disabled={bulkSaving}
                >
                  {bulkSaving ? 'Saving...' : 'Save All Changes'}
                </button>
                <button
                  className="modal-close"
                  onClick={() => !bulkSaving && setShowBulkEdit(false)}
                  disabled={bulkSaving}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="modal-body modal-body-bulk">
              <div className="bulk-column-header">
                <div>Preview</div>
                <div>Description (Primary)</div>
                <div>Custom Label [1] (Headline)</div>
                <div>Custom Label [2] (Description)</div>
              </div>
              <div className="bulk-table-wrap">
                <table className="bulk-table">
                  <tbody>
                    {bulkEditRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="bulk-preview-wrap">
                            <img src={row.public_url || ''} alt={row.original_filename} className="bulk-preview-thumb" />
                            <div className="bulk-preview-popover">
                              <img src={row.public_url || ''} alt={row.original_filename} className="bulk-preview-large" />
                            </div>
                          </div>
                        </td>
                        <td>
                          <textarea
                            className="bulk-textarea"
                            value={row.description_primary}
                            onChange={(e) => updateBulkRow(row.id, 'description_primary', e.target.value)}
                            rows={3}
                          />
                          {getExceededMessage(row.description_primary, META_LIMITS.primaryDescription) && (
                            <small style={{ color: 'var(--color-destructive)', fontSize: '0.8rem' }}>
                              {getExceededMessage(row.description_primary, META_LIMITS.primaryDescription)}
                            </small>
                          )}
                        </td>
                        <td>
                          <textarea
                            className="bulk-textarea"
                            value={row.custom_label_1}
                            onChange={(e) => updateBulkRow(row.id, 'custom_label_1', e.target.value)}
                            rows={3}
                          />
                          {getExceededMessage(row.custom_label_1, META_LIMITS.headline) && (
                            <small style={{ color: 'var(--color-destructive)', fontSize: '0.8rem' }}>
                              {getExceededMessage(row.custom_label_1, META_LIMITS.headline)}
                            </small>
                          )}
                        </td>
                        <td>
                          <textarea
                            className="bulk-textarea"
                            value={row.custom_label_2}
                            onChange={(e) => updateBulkRow(row.id, 'custom_label_2', e.target.value)}
                            rows={3}
                          />
                          {getExceededMessage(row.custom_label_2, META_LIMITS.description) && (
                            <small style={{ color: 'var(--color-destructive)', fontSize: '0.8rem' }}>
                              {getExceededMessage(row.custom_label_2, META_LIMITS.description)}
                            </small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignDetail;
